<script>
    import { onMount } from 'svelte';
    import { Link, navigate } from 'svelte-routing';
    import { Tooltip } from 'sveltestrap';

    import { vptServer, isAuthenticated, selectedInmate, selectedInmateDateArrived, selectedInmateDateDeparted, selectedDateRange } from '../stores.js';
    import { getApiDateFormat } from '../date-time.js';
    
    import AimToast from '../component/AimToast.svelte';

    import InmateDrawer from './drawer/InmateDrawer.svelte';
    import InmateSummary from './InmateSummary.svelte';
    import InmateHistory from './InmateHistory.svelte';
    import IsolationWidget from './isolation/IsolationWidget.svelte';
    import IsolationHistory from './isolation/IsolationHistory.svelte';
    import EarlyDaysWidget from './early-days/EarlyDaysWidget.svelte';
    import EarlyDaysHistory from './early-days/EarlyDaysHistory.svelte';
    import SurveyWidget from './survey/SurveyWidget.svelte';
    import SurveyHistory from './survey/SurveyHistory.svelte';
    import EarlyRiskWidget from './early-risk/EarlyRiskWidget.svelte';
    import EarlyRiskHistory from './early-risk/EarlyRiskHistory.svelte';
    import RiskFactorsWidget from './risk-factors/RiskFactorsWidget.svelte';
    import RiskFactorHistory from './risk-factors/RiskFactorHistory.svelte';

    export let id;

    let selectedInmateAspect = "Overall";
    let toastProperties = null;
    let todaysConfig;
    let drawerOffset = 261;

    let showArchiveMenu = false;
    let isEarlyRiskArchived = false;
    let isEarlyDaysArchived = false;

    onMount(() => {
        selectedInmate.set("");
        selectedInmateDateArrived.set(null);
        selectedInmateDateDeparted.set(null);

        if (!($isAuthenticated)) {
            navigate("/");
        }
        getConfig();
    });

    async function getConfig() {
        await fetch(`${$vptServer}/configuration/${getApiDateFormat(new Date())}`)
            .then(r => r.json())
            .then(data => {
                todaysConfig = data;
                selectedDateRange.set(todaysConfig.isolationPastDaysToAnalyse);
            });
    }

    function showToast(event) {
        toastProperties = event.detail.toastProperties;
    }

    function refresh() {
        todaysConfig = todaysConfig;
    }
</script>

<style>
    :global(.inmate-banner) {
        top: 50px;
    }

    :global(.inmate-widget) {
        margin-left: 8px;
        flex-grow: 0;
        flex-shrink: 0;
    }

    /* List Widgets */
    :global(.list-widget) {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    :global(.list-widget-body) {
        display: flex;
        flex-direction: column;
        flex: 0 0 86px;
    }
    
    :global(.list-widget-footer) {
        display: block;
        width: 100%;
        text-align: center;
        font-size: 12px;
        line-height: 14px;
    }

    .archive {
        margin-left: auto;
    }

    .archive .dropdown {
        margin: 0 15px 0 8px;
    }

    .archive button.dropdown-toggle {
        padding: 8px 10px 8px 8px;
        color: #676a6d;
        border-color: #676a6d
    }

    .archive button.dropdown-toggle.active {
        background-color: #676a6d;
        color: white;
    }

    .archive button.dropdown-item {
        padding: 5px 20px;
        font-size: 14px;
    }

    .archive button span {
        font-size: 16px;
        line-height: 16px;
        vertical-align: -2px;
    }
</style>

<nav class="navbar fixed-top p-0">
    <Link id="logo" to="/dashboard" class="focus-outline">
        <img src="/assets/logo-aim-target-white.png" alt="AIM Logo" />
    </Link>
    
    <h1>
        {#if todaysConfig}
            <Link class="focus-outline" to="/dashboard">{todaysConfig.establishmentName}</Link> &middot;
            <button type="button" class="button-reset focus-outline" on:click={refresh}>{$selectedInmate}</button>
            {#if selectedInmateAspect != "Overall"} &middot; {selectedInmateAspect}{/if}
        {/if}
    </h1>

    <a id="user-guide-inmate" class="ml-auto mr-3 focus-outline" href="/assets/docs/AIM_User_Guide.pdf" target="_blank">
        <img src="/assets/file.svg" alt="user guide" />
    </a>
    <Tooltip target="user-guide-inmate" placement="bottom">Download User Guide</Tooltip>
</nav>

<div class="banner inmate-banner">
    {#if todaysConfig}
        <InmateSummary {id} {todaysConfig} {selectedInmateAspect}
            on:click={() => {selectedInmateAspect = "Overall"; drawerOffset = 261}} />

        <EarlyRiskWidget {id} {todaysConfig} {selectedInmateAspect} 
            on:click={() => {selectedInmateAspect = "Early Risk"; drawerOffset = 218}}
            on:dataLoad={(event) => isEarlyRiskArchived = event.detail.isAspectArchived} />

        <EarlyDaysWidget {id} {todaysConfig} {selectedInmateAspect} 
            on:click={() => {selectedInmateAspect = `${todaysConfig.earlyDaysLabelName} Engagement`; drawerOffset = 218}} 
            on:dataLoad={(event) => isEarlyDaysArchived = event.detail.isAspectArchived} />

        <IsolationWidget {id} {todaysConfig} {selectedInmateAspect} 
            on:click={() => {selectedInmateAspect = "Isolation"; drawerOffset = 218}} />

        <SurveyWidget {id} {todaysConfig} {selectedInmateAspect}
            on:click={() => {selectedInmateAspect = todaysConfig.surveyLabelName; drawerOffset = 218}} 
            on:schedule={showToast} />

        <RiskFactorsWidget {id} {todaysConfig} {selectedInmateAspect} 
            on:click={() => {selectedInmateAspect = "Risk Factors"; drawerOffset = 218}} />
        
        {#if isEarlyRiskArchived || isEarlyDaysArchived}
            <div class="archive">
                <div class="dropdown dropleft">
                    <button on:click={() => showArchiveMenu = !showArchiveMenu} type="button" id="archiveButton" aria-haspopup="true"
                            class:active={showArchiveMenu} class="btn btn-secondary dropdown-toggle">
                        <span><i class="bi bi-archive-fill" /></span>
                    </button>

                    <div class="dropdown-menu" class:show={showArchiveMenu} aria-labelledby="archiveButton">
                        {#if isEarlyRiskArchived}
                            <button class="button-reset focus-outline dropdown-item" 
                                    on:click={() => {selectedInmateAspect = "Early Risk"; showArchiveMenu = false; drawerOffset = 218}}>
                                Early Risk
                            </button>
                        {/if}

                        {#if isEarlyDaysArchived}
                            <button class="button-reset focus-outline dropdown-item" 
                                    on:click={() => {selectedInmateAspect = `${todaysConfig.earlyDaysLabelName} Engagement`; showArchiveMenu = false; drawerOffset = 218}}>
                                Early Days
                            </button>
                        {/if}
                    </div>
                </div>
            </div>
        {/if}
    {/if}
</div>

{#if todaysConfig}
    {#if selectedInmateAspect === "Overall"}
        <InmateHistory {id} {todaysConfig} />
    {/if}
    {#if selectedInmateAspect === "Early Risk"}
        <EarlyRiskHistory {id} {todaysConfig} />
    {/if}
    {#if todaysConfig && selectedInmateAspect === `${todaysConfig.earlyDaysLabelName} Engagement`}
        <EarlyDaysHistory {id} {todaysConfig} />
    {/if}
    {#if selectedInmateAspect === "Isolation"}
        <IsolationHistory {id} {todaysConfig} />
    {/if}
    {#if todaysConfig && selectedInmateAspect === todaysConfig.surveyLabelName}
        <SurveyHistory {id} {todaysConfig} />
    {/if}
    {#if selectedInmateAspect === "Risk Factors"}
        <RiskFactorHistory {id} {todaysConfig} />
    {/if}

    <InmateDrawer {id} {todaysConfig} top={drawerOffset} />
{/if}

{#if todaysConfig && todaysConfig.footerText}
    <footer class="footer fixed-bottom d-flex justify-content-center">
        <p class="my-2">{todaysConfig.footerText}</p>
    </footer>
{/if}

<AimToast {...toastProperties}></AimToast>